
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useDrag } from '@use-gesture/react';
import { API_URL as API } from '@/utils/apiConfig';
import {
  Palette, BookOpen, Shield, X, Lock, Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useTheme, themes as darkThemes, lightThemes } from '@/contexts/ThemeContext';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import Header from '@/components/Header';
import { subscribeToPush } from '@/services/pushNotifications';
import { SettingsSearchProvider, useSettingsSearch } from '@/contexts/SettingsSearchContext';

import { PreferencesTab } from './settings/PreferencesTab';
import { StudyToolsTab } from './settings/StudyToolsTab';
import { AccountTab } from './settings/AccountTab';

const SETTINGS_TABS = ['preferences', 'study', 'account'];

export default function SettingsPage({ user, asPanel = false, onClose }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    currentTheme, changeTheme,
    currentFontSize, changeFontSize,
    currentFontFamily, changeFontFamily,
    fontFamilies,
    colorMode, changeColorMode,
  } = useTheme();

  const { isPaid, tier, isShareOwner, isShareMember, shareOwnerName, subscription } = useSubscription();
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [translationsData, setTranslationsData] = useState([]);
  const [commentators, setCommentators] = useState([]);
  const [selectedTranslations, setSelectedTranslations] = useState([]);
  const [selectedCommentators, setSelectedCommentators] = useState([]);
  const [greekAnalysisEnabled, setGreekAnalysisEnabled] = useState(false);
  const [historicalContextEnabled, setHistoricalContextEnabled] = useState(false);
  const [deepStudyEnabled, setDeepStudyEnabled] = useState(false);
  const [simpleLanguageEnabled, setSimpleLanguageEnabled] = useState(false);
  const [commentaryExpanded, setCommentaryExpanded] = useState(false);
  const [language, setLanguage] = useState(i18n.language);
  const [recapDay, setRecapDay] = useState('');
  const [recapTime, setRecapTime] = useState('');
  const [recapTimezone, setRecapTimezone] = useState('');
  const [pushRecapEnabled, setPushRecapEnabled] = useState(true);
  const [pushReflectionEnabled, setPushReflectionEnabled] = useState(false);
  const userInteracted = useRef(false);

  // Device management
  const [devices, setDevices] = useState([]);
  const [maxDevices, setMaxDevices] = useState(5);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [removingDevice, setRemovingDevice] = useState(null);

  // Subscription management
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);
  const [settingsTab, setSettingsTab] = useState('preferences');
  const stripRef = useRef(null);
  const slideRefs = useRef([]);
  const [carouselHeight, setCarouselHeight] = useState('auto');

  useEffect(() => {
    const strip = stripRef.current;
    if (strip) {
      strip.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      strip.style.transform = `translate3d(-${SETTINGS_TABS.indexOf(settingsTab) * (100 / 3)}%, 0, 0)`;
    }
    const idx = SETTINGS_TABS.indexOf(settingsTab);
    const el = slideRefs.current[idx];
    if (el) setCarouselHeight(el.scrollHeight);
  }, [settingsTab]);

  useEffect(() => {
    const idx = SETTINGS_TABS.indexOf(settingsTab);
    const el = slideRefs.current[idx];
    if (!el) return;
    const ro = new ResizeObserver(() => setCarouselHeight(el.scrollHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [settingsTab]);

  const bindSwipe = useDrag(({ active, movement: [mx], direction: [dx], velocity: [vx] }) => {
    const strip = stripRef.current;
    if (!strip) return;
    const idx = SETTINGS_TABS.indexOf(settingsTab);
    if (active) {
      strip.style.transition = 'none';
      strip.style.transform = `translate3d(calc(-${idx * (100 / 3)}% + ${mx}px), 0, 0)`;
    } else {
      strip.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      if ((mx < -60 || (vx > 0.3 && dx < 0)) && idx < SETTINGS_TABS.length - 1) {
        setSettingsTab(SETTINGS_TABS[idx + 1]);
      } else if ((mx > 60 || (vx > 0.3 && dx > 0)) && idx > 0) {
        setSettingsTab(SETTINGS_TABS[idx - 1]);
      } else {
        strip.style.transform = `translate3d(-${idx * (100 / 3)}%, 0, 0)`;
      }
    }
  }, { axis: 'x', filterTaps: true });

  // PWA Install
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone) { setIsAppInstalled(true); return; }
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => setIsAppInstalled(true);
    window.addEventListener('appinstalled', installed);
    return () => { window.removeEventListener('beforeinstallprompt', handler); window.removeEventListener('appinstalled', installed); };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') { setIsAppInstalled(true); setInstallPrompt(null); }
    } else {
      setShowIOSInstructions(prev => !prev);
    }
  };

  // Account management
  const [accountInfo, setAccountInfo] = useState(null);
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const profilePicRef = useRef(null);

  const fetchAccountInfo = async () => {
    try {
      const res = await axios.get(`${API}/auth/account-info`, { withCredentials: true });
      setAccountInfo(res.data);
      setUsername(res.data.username || '');
    } catch { /* guest or error */ }
  };

  const handleProfilePicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('generated.settingspage_please_select_an_8902')); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error(t('generated.settingspage_image_must_be_bd03')); return; }
    setUploadingPicture(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await axios.post(`${API}/auth/upload-profile-picture`, { image_data: reader.result }, { withCredentials: true });
          setAccountInfo((prev) => ({ ...prev, picture: res.data.picture }));
          toast.success(t('generated.settingspage_profile_picture_updated_2bc5'));
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to upload');
        }
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error(t('generated.settingspage_failed_to_read_7982'));
      setUploadingPicture(false);
    }
  };

  const handleSaveUsername = async () => {
    try {
      const res = await axios.put(`${API}/auth/username`, { username: username.trim() }, { withCredentials: true });
      toast.success(res.data.message);
      setAccountInfo(prev => ({ ...prev, username: res.data.username }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update username');
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t('generated.settingspage_passwords_do_not_946a')); return; }
    setPasswordLoading(true);
    try {
      const res = await axios.put(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      toast.success(res.data.message);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPasswordLoading(false); }
  };

  const handleSetPassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t('generated.settingspage_passwords_do_not_946a')); return; }
    setPasswordLoading(true);
    try {
      const res = await axios.post(`${API}/auth/set-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(res.data.message);
      setNewPassword(''); setConfirmPassword('');
      setAccountInfo(prev => ({ ...prev, has_password: true, auth_method: 'google+email' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally { setPasswordLoading(false); }
  };

  const fetchDevices = async () => {
    try {
      setDevicesLoading(true);
      const res = await axios.get(`${API}/subscription/devices`, { withCredentials: true });
      setDevices(res.data.devices || []);
      setMaxDevices(res.data.max_devices || 3);
      setCurrentDeviceId(res.data.current_device_id || null);
    } catch {
      // Silently fail for guests/free users
    } finally {
      setDevicesLoading(false);
    }
  };

  const removeDevice = async (deviceId) => {
    try {
      setRemovingDevice(deviceId);
      await axios.delete(`${API}/subscription/devices/${deviceId}`, { withCredentials: true });
      setDevices(prev => prev.filter(d => d.device_id !== deviceId));
      toast.success(t('settings.device_removed', 'Device removed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.device_remove_error', 'Failed to remove device'));
    } finally {
      setRemovingDevice(null);
    }
  };

  const renameDevice = async (deviceId, newName) => {
    try {
      await axios.put(`${API}/settings/devices/${deviceId}/name`, { name: newName }, { withCredentials: true });
      setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, device_name: newName } : d));
      toast.success(t('settings.device_renamed', 'Device renamed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.device_rename_error', 'Failed to rename device'));
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/payments/subscription`, { withCredentials: true });
      setSubscriptionInfo(res.data);
    } catch { /* Silently fail */ }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your billing period.')) return;
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/payments/cancel`, {}, { withCredentials: true });
      toast.success(t('generated.settingspage_subscription_will_cancel_364a'));
      setSubscriptionInfo(prev => ({ ...prev, status: 'canceling', cancel_at_period_end: true, current_period_end: res.data.access_until }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel subscription');
    } finally { setCancelLoading(false); }
  };

  const fetch2FAStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true });
      setTwoFAEnabled(res.data.totp_enabled);
    } catch {}
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
      setTwoFASetup(res.data);
      setTwoFACode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start 2FA setup');
    } finally { setTwoFALoading(false); }
  };

  const handleVerify2FA = async () => {
    setTwoFALoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: twoFACode }, { withCredentials: true });
      setTwoFAEnabled(true);
      setTwoFASetup(null);
      setTwoFACode('');
      toast.success(t('generated.settingspage_two_factor_authentication_f2e1'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally { setTwoFALoading(false); }
  };

  const handleDisable2FA = async () => {
    if (!twoFACode) { toast.error(t('generated.settingspage_enter_your_authenticator_99e5')); return; }
    setTwoFALoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: twoFACode }, { withCredentials: true });
      setTwoFAEnabled(false);
      setTwoFACode('');
      toast.success(t('generated.settingspage_two_factor_authentication_6dbd'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally { setTwoFALoading(false); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, transRes, commRes] = await Promise.all([
          axios.get(`${API}/settings`, { withCredentials: true }),
          axios.get(`${API}/bible/translations`),
          axios.get(`${API}/commentators`),
        ]);
        const s = settingsRes.data;
        setSelectedTranslations(s.selected_translations || []);
        setSelectedCommentators(s.selected_commentators || []);
        setGreekAnalysisEnabled(s.greek_analysis_enabled || false);
        setHistoricalContextEnabled(s.historical_context_enabled || false);
        setDeepStudyEnabled(s.deep_study_enabled || false);
        setSimpleLanguageEnabled(s.simple_language || false);
        if (!userInteracted.current) {
          if (s.color_theme && !localStorage.getItem('reveala-theme')) changeTheme(s.color_theme);
          if (s.font_size && !localStorage.getItem('reveala-font-size')) changeFontSize(s.font_size);
          if (s.font_family && !localStorage.getItem('reveala-font-family')) changeFontFamily(s.font_family);
        }
        if (s.language) {
          // If the user has explicitly picked a language on this
          // device (e.g. via the landing-page picker before signing
          // in), the client is the source of truth — the Settings
          // dropdown should read i18n.language, NOT the stale server
          // value ('en' by default). Only the "server wins" branch
          // pulls the server language down into the local dropdown +
          // active i18n language.
          const explicit = localStorage.getItem('eili-lang-explicit');
          if (explicit) {
            setLanguage(i18n.language);
            // Fire-and-forget: push the client choice back to the
            // server so this language becomes durable across devices
            // and matches what the server thinks going forward.
            if (i18n.language !== s.language) {
              axios.patch(`${API}/settings`, { language: i18n.language }, { withCredentials: true }).catch(() => { /* offline / unauth */ });
            }
          } else {
            setLanguage(s.language);
            i18n.changeLanguage(s.language);
          }
        }
        if (s.recap_day && s.recap_day !== 'skipped') setRecapDay(s.recap_day);
        if (s.recap_time) setRecapTime(s.recap_time);
        if (s.recap_timezone) setRecapTimezone(s.recap_timezone);
        if (s.push_recap_enabled !== undefined) setPushRecapEnabled(s.push_recap_enabled);
        if (s.push_reflection_enabled !== undefined) setPushReflectionEnabled(s.push_reflection_enabled);
        setTranslationsData(transRes.data);
        setCommentators(commRes.data);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error(t('generated.settingspage_failed_to_load_6bca'));
      }
    };
    fetchData();
    if (isPaid) fetchDevices();
    if (!user?.is_guest) fetchSubscription();
    if (!user?.is_guest) fetch2FAStatus();
    if (!user?.is_guest) fetchAccountInfo();
    if (!user?.is_guest) {
      axios.get(`${API}/settings/admin-access`, { withCredentials: true })
        .then(res => setHasAdminAccess(res.data.has_admin_access))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (recapDay && recapTime && recapTimezone && userInteracted.current) {
      subscribeToPush().catch(() => {});
    }
  }, [recapDay, recapTime, recapTimezone]);

  const updateSetting = async (key, value) => {
    try {
      const payload = { [key]: value };
      if (key === 'selected_translations') payload.primary_translation = value.length > 0 ? value[0] : 'asv';
      if (key === 'language' && value !== i18n.language) {
        i18n.changeLanguage(value);
        // Mark as explicit so applyUserSettings on next mount does
        // not treat the server value as authoritative.
        try { localStorage.setItem('eili-lang-explicit', '1'); } catch { /* private mode */ }
      }
      await axios.put(`${API}/settings`, payload, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save setting:', error);
      toast.error(t('settings.failed_save'));
    }
  };

  const toggleTranslation = (id) => {
    const next = selectedTranslations.includes(id) ? selectedTranslations.filter(t => t !== id) : [...selectedTranslations, id];
    setSelectedTranslations(next);
    updateSetting('selected_translations', next);
  };

  const toggleCommentator = (id) => {
    const next = selectedCommentators.includes(id) ? selectedCommentators.filter(c => c !== id) : [...selectedCommentators, id];
    setSelectedCommentators(next);
    updateSetting('selected_commentators', next);
    // Also sync the enabled flag so the chat pipeline includes commentaries
    updateSetting('commentators_enabled', next.length > 0);
  };

  const handleThemeChange = (key) => { userInteracted.current = true; changeTheme(key); updateSetting('color_theme', key); };
  const handleFontSizeChange = (size) => { userInteracted.current = true; changeFontSize(size); updateSetting('font_size', size); };
  const handleFontFamilyChange = (fontName) => { userInteracted.current = true; changeFontFamily(fontName); updateSetting('font_family', fontName); };
  const handleLanguageChange = (lang) => { userInteracted.current = true; setLanguage(lang); updateSetting('language', lang); };
  const handleColorModeChange = (mode) => { userInteracted.current = true; changeColorMode(mode); updateSetting('color_mode', mode); };

  const allThemeSwatches = Object.keys(darkThemes).map((key) => ({
    key,
    label: darkThemes[key].name,
    darkBg: darkThemes[key].background,
    darkAccent: darkThemes[key].accent,
    lightBg: lightThemes[key].background,
    lightAccent: lightThemes[key].accent,
  }));

  const slideClass = asPanel ? "px-4 py-5 space-y-5 pb-6" : "max-w-xl lg:max-w-2xl mx-auto px-4 py-8 space-y-5 pb-6";

  const tabNavigation = <SettingsTabsWithBadges tabs={[
    { id: 'preferences', label: t('settings.tab_preferences', 'Preferences'), icon: Palette },
    { id: 'study', label: t('settings.tab_study', 'Study Tools'), icon: BookOpen },
    { id: 'account', label: t('settings.tab_account', 'Account'), icon: Shield },
  ]} settingsTab={settingsTab} setSettingsTab={setSettingsTab} />;

  const searchBar = <SettingsSearchInput placeholder={t('settings.search_placeholder', 'Search settings…')} />;

  const settingsContent = (
    <>
    <div className="overflow-hidden" style={{ height: carouselHeight, transition: 'height 0.35s cubic-bezier(0.32, 0.72, 0, 1)' }}>
      <div ref={stripRef} {...bindSwipe()} className="flex touch-pan-y" style={{ width: '300%', willChange: 'transform', alignItems: 'flex-start' }}>
        <div ref={el => slideRefs.current[0] = el} style={{ width: '33.333%' }} className={slideClass}>
          <PreferencesTab
            colorMode={colorMode} handleColorModeChange={handleColorModeChange}
            currentTheme={currentTheme} handleThemeChange={handleThemeChange}
            themeSwatches={allThemeSwatches} fontFamilies={fontFamilies}
            currentFontFamily={currentFontFamily} handleFontFamilyChange={handleFontFamilyChange}
            currentFontSize={currentFontSize} handleFontSizeChange={handleFontSizeChange}
            language={language} handleLanguageChange={handleLanguageChange}
            updateSetting={updateSetting}
            isPaid={isPaid} setUpgradeFeature={setUpgradeFeature}
            Lock={Lock}
            currentDeviceName={devices.find(d => d.device_id === currentDeviceId)?.device_name}
            recapDay={recapDay} setRecapDay={setRecapDay}
            recapTime={recapTime} setRecapTime={setRecapTime}
            recapTimezone={recapTimezone} setRecapTimezone={setRecapTimezone}
            pushRecapEnabled={pushRecapEnabled} setPushRecapEnabled={setPushRecapEnabled}
            pushReflectionEnabled={pushReflectionEnabled} setPushReflectionEnabled={setPushReflectionEnabled}
            user={user}
          />
        </div>
        <div ref={el => slideRefs.current[1] = el} style={{ width: '33.333%' }} className={slideClass}>
          <StudyToolsTab
            selectedTranslations={selectedTranslations} toggleTranslation={toggleTranslation}
            greekAnalysisEnabled={greekAnalysisEnabled} setGreekAnalysisEnabled={setGreekAnalysisEnabled}
            historicalContextEnabled={historicalContextEnabled} setHistoricalContextEnabled={setHistoricalContextEnabled}
            deepStudyEnabled={deepStudyEnabled} setDeepStudyEnabled={setDeepStudyEnabled}
            simpleLanguageEnabled={simpleLanguageEnabled} setSimpleLanguageEnabled={setSimpleLanguageEnabled}
            selectedCommentators={selectedCommentators} toggleCommentator={toggleCommentator} setSelectedCommentators={setSelectedCommentators}
            commentators={commentators} commentaryExpanded={commentaryExpanded} setCommentaryExpanded={setCommentaryExpanded}
            isPaid={isPaid} setUpgradeFeature={setUpgradeFeature} updateSetting={updateSetting}
          />
        </div>
        <div ref={el => slideRefs.current[2] = el} style={{ width: '33.333%' }} className={slideClass}>
          <AccountTab
            user={user} accountInfo={accountInfo} username={username} setUsername={setUsername}
            handleSaveUsername={handleSaveUsername} handleProfilePicture={handleProfilePicture}
            uploadingPicture={uploadingPicture} profilePicRef={profilePicRef}
            currentPassword={currentPassword} setCurrentPassword={setCurrentPassword}
            newPassword={newPassword} setNewPassword={setNewPassword}
            confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
            handleChangePassword={handleChangePassword} handleSetPassword={handleSetPassword}
            passwordLoading={passwordLoading}
            twoFAEnabled={twoFAEnabled} twoFASetup={twoFASetup} setTwoFASetup={setTwoFASetup}
            twoFACode={twoFACode} setTwoFACode={setTwoFACode} twoFALoading={twoFALoading}
            handleSetup2FA={handleSetup2FA} handleVerify2FA={handleVerify2FA} handleDisable2FA={handleDisable2FA}
            isPaid={isPaid} tier={tier} isShareOwner={isShareOwner}
            isShareMember={isShareMember} shareOwnerName={shareOwnerName} subscription={subscription}
            subscriptionInfo={subscriptionInfo} cancelLoading={cancelLoading}
            handleCancelSubscription={handleCancelSubscription}
            devices={devices} devicesLoading={devicesLoading} maxDevices={maxDevices}
            currentDeviceId={currentDeviceId} removingDevice={removingDevice} removeDevice={removeDevice} renameDevice={renameDevice}
            showLicenses={showLicenses} setShowLicenses={setShowLicenses}
            showDeleteModal={showDeleteModal} setShowDeleteModal={setShowDeleteModal}
            hasAdminAccess={hasAdminAccess}
            installPrompt={installPrompt} isAppInstalled={isAppInstalled}
            isIOS={isIOS} showIOSInstructions={showIOSInstructions} handleInstallClick={handleInstallClick}
            onClose={onClose}
          />
        </div>
      </div>
    </div>

      {upgradeFeature && (
        <UpgradePrompt feature={upgradeFeature} onClose={() => setUpgradeFeature(null)} />
      )}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          authMethod={user?.auth_method || 'email'}
        />
      )}
    </>
  );

  if (asPanel) {
    return (
      <SettingsSearchProvider>
        <div className="flex flex-col h-full bg-card text-card-foreground w-full lg:w-[500px] transition-all duration-300" data-testid="settings-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b theme-border shrink-0">
            <div>
              <h2 className="text-base font-semibold theme-text">{t('settings.title', 'Settings')}</h2>
              {accountInfo && (accountInfo.username || accountInfo.name) && (
                <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }} data-testid="settings-user-identifier">
                  {accountInfo.username ? `@${accountInfo.username}` : accountInfo.name}
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full hover:bg-[var(--theme-hover)] theme-text-secondary" data-testid="settings-panel-close">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="px-4 pt-4 pb-2 shrink-0 space-y-3" style={{ background: 'var(--theme-surface, var(--theme-background))' }}>
            {searchBar}
            {tabNavigation}
          </div>
          <div className="flex-1 overflow-y-auto">
            {settingsContent}
          </div>
        </div>
      </SettingsSearchProvider>
    );
  }

  return (
    <SettingsSearchProvider>
      <div className="page-container" style={{ background: 'var(--theme-background)' }}>
        <Header user={user} />
        {accountInfo && (accountInfo.username || accountInfo.name) && (
          <div className="max-w-xl lg:max-w-2xl mx-auto px-4 pt-3">
            <p className="text-sm font-medium" style={{ color: 'var(--theme-textSecondary)' }} data-testid="settings-user-identifier">
              {accountInfo.username ? `@${accountInfo.username}` : accountInfo.name}
            </p>
          </div>
        )}
        <div className="max-w-xl lg:max-w-2xl mx-auto px-4 pt-4 sticky top-0 z-10 w-full space-y-3" style={{ background: 'var(--theme-background)', paddingTop: '1rem' }}>
          {searchBar}
          {tabNavigation}
        </div>
        <div className="page-content pb-24">
          {settingsContent}
        </div>
      </div>
    </SettingsSearchProvider>
  );
}

// ── Search input + tabs-with-badges helpers (consume the provider) ───────

function SettingsSearchInput({ placeholder }) {
  const {
    t
  } = useTranslation();

  const { query, setQuery } = useSettingsSearch();
  return (
    <div
      className="flex items-center gap-2 rounded-2xl px-3"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
      }}
      data-testid="settings-search"
    >
      <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--theme-textSecondary)' }} />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent border-0 outline-none py-2.5 text-sm"
        style={{ color: 'var(--theme-text)', fontSize: '16px' }}
        data-testid="settings-search-input"
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery('')}
          className="p-1 rounded-md"
          aria-label={t('generated.settingspage_clear_search_908a')}
          data-testid="settings-search-clear"
        >
          <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>
      )}
    </div>
  );
}

function SettingsTabsWithBadges({ tabs, settingsTab, setSettingsTab }) {
  const { query, matchCounts } = useSettingsSearch();

  // Auto-jump to the first tab with matches when the current tab has none.
  // Helps the user discover hits across tabs without manual navigation.
  React.useEffect(() => {
    if (!query) return;
    const currentCount = matchCounts[settingsTab] || 0;
    if (currentCount === 0) {
      const firstWithMatch = tabs.find((t) => (matchCounts[t.id] || 0) > 0);
      if (firstWithMatch && firstWithMatch.id !== settingsTab) {
        setSettingsTab(firstWithMatch.id);
      }
    }
  }, [query, matchCounts, tabs, settingsTab, setSettingsTab]);

  return (
    <div className="flex rounded-2xl p-1 gap-1" style={{ background: 'var(--glass-bg-active)' }} data-testid="settings-tabs">
      {tabs.map((tab) => {
        const count = query ? (matchCounts[tab.id] || 0) : null;
        return (
          <button
            key={tab.id}
            className="flex-1 flex items-center justify-center gap-1 py-2 px-1 rounded-xl font-medium transition-all overflow-hidden"
            style={{ background: settingsTab === tab.id ? 'var(--theme-accent)' : 'transparent', color: settingsTab === tab.id ? '#fff' : 'var(--theme-textSecondary)', fontSize: '12px' }}
            onClick={() => setSettingsTab(tab.id)}
            data-testid={`settings-tab-${tab.id}`}
          >
            <tab.icon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{tab.label}</span>
            {count !== null && count > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                style={{
                  background: settingsTab === tab.id ? 'rgba(255,255,255,0.25)' : 'var(--theme-accent)',
                  color: settingsTab === tab.id ? '#fff' : '#fff',
                }}
                data-testid={`settings-tab-${tab.id}-match-count`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
